import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    CopilotBar as CopilotBarComponent,
    CopilotContext,
    COPILOT_BAR_LOADING_STATES,
} from '@Sage/ssm-web5';

import { COPILOT_BAR } from 'constants/Features';
import useCopilot from 'hooks/useCopilot';
import { getActiveCrudName } from 'utils/crud';
import { isEqual } from 'utils/objects';
import { userflowHideAndShow } from 'utils/userflow';

import { getScenarioDetectors } from './scenarios';

import './styles.scss';

const COPILOT_BAR_ON_CLASS = 'copilot-bar--on';
const COPILOT_BAR_LOADING_CLASS = 'copilot-bar--loading';
const COPILOT_BAR_MINIMIZED_CLASS = 'copilot-bar--minimized';

const INITIAL_COPILOT_BAR_STATE = {
    chatSetup: null,
    doneMessage: null,
    height: 0,
    initialLoadingPhrase: null,
    inputPlaceholder: null,
    isMinimized: false,
    isOpen: false,
    loadingPhrases: [],
    loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
    padding: 24,
    pinnedToRight: false,
    transition: 'fade',
    width: '100%',
};

const CopilotBar = () => {
    // ========================================
    // HOOKS
    // ========================================
    const dispatch = useDispatch();
    const location = useLocation();
    const copilotContext = useContext(CopilotContext);
    const { copilotBarProps, featureMethods, setCopilotBarProps } = copilotContext;
    const { isMinimized, isOpen, loadingState } = copilotBarProps;

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const entityModalState = useSelector((state) => state.entityModal || {});
    const entityCrud = useSelector((state) => state.entityCrud || {});
    const activeCrudName = useSelector((state) => getActiveCrudName(state));
    const entityCrudIsOpen = useSelector((state) => state.entityCrud?.isOpen || false);
    const isCopilotBarEnabled = useSelector(
        (state) => state.config.flagsmith?.[COPILOT_BAR] || false,
    );

    // ========================================
    // LOCAL STATE & REFS
    // ========================================
    const prevStateRef = useRef(INITIAL_COPILOT_BAR_STATE);
    const entityCrudStateRef = useRef({ activeCrudName: null, entityCrud: {} });
    const pendingScenarioConfigRef = useRef(null);
    const hasShownDisclaimerRef = useRef(false);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================

    // Memoize state objects to prevent unnecessary recalculations.
    const appState = useMemo(
        () => ({
            entityCrudState: {
                activeCrudName,
                entityCrud,
                isOpen: entityCrudIsOpen,
            },
            entityModalState,
            featureMethods,
            pathname: location.pathname,
        }),
        [
            activeCrudName,
            entityCrud,
            entityCrudIsOpen,
            entityModalState,
            featureMethods,
            location.pathname,
        ],
    );

    // Keep ref updated with latest entity CRUD state for getInteractions callback
    entityCrudStateRef.current = { activeCrudName, entityCrud };

    // Getter function to retrieve fresh entity CRUD state
    const getEntityCrudState = () => entityCrudStateRef.current;

    // Check if there's a matching scenario (regardless of disclaimer status)
    const matchingScenario = useMemo(() => {
        if (!isCopilotBarEnabled) return null;
        return getScenarioDetectors().find((d) => d.condition(appState)) || null;
    }, [appState, isCopilotBarEnabled]);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================

    // Called after disclaimer is accepted - apply the pending scenario config
    const onConfirmDisclaimerSuccess = useCallback(() => {
        if (pendingScenarioConfigRef.current && setCopilotBarProps) {
            // Apply the pending config and update prevStateRef to keep sync
            prevStateRef.current = pendingScenarioConfigRef.current;
            setCopilotBarProps(pendingScenarioConfigRef.current);
            pendingScenarioConfigRef.current = null;
        }
    }, [setCopilotBarProps]);

    const {
        copilotActivatedDisclaimer,
        copilotDisclaimerOnClose,
        renderCopilotDisclaimer,
        setCopilotModalAfterDisclaimer,
    } = useCopilot({
        onConfirmDisclaimerSuccess,
        overrideModalOverlayClassName: 'fm-copilot-bar__disclaimer-overlay',
    });

    // ========================================
    // DERIVED STATE (continued)
    // ========================================

    const copilotBarState = useMemo(() => {
        // No matching scenario or feature disabled
        if (!matchingScenario) return INITIAL_COPILOT_BAR_STATE;

        // Scenario matches but disclaimer not accepted - don't show bar yet
        if (!copilotActivatedDisclaimer) return INITIAL_COPILOT_BAR_STATE;

        // Let the detector build its own configuration
        const config = matchingScenario.buildConfig({
            dispatch,
            getEntityCrudState,
            setCopilotBarProps,
            state: appState,
        });

        return config || INITIAL_COPILOT_BAR_STATE;
    }, [appState, copilotActivatedDisclaimer, dispatch, matchingScenario, setCopilotBarProps]);

    // ========================================
    // EFFECTS
    // ========================================

    // Show disclaimer when scenario matches but disclaimer not accepted
    useEffect(() => {
        if (matchingScenario && !copilotActivatedDisclaimer && !hasShownDisclaimerRef.current) {
            // Build and store the pending config for after disclaimer is accepted
            const config = matchingScenario.buildConfig({
                dispatch,
                getEntityCrudState,
                setCopilotBarProps,
                state: appState,
            });
            pendingScenarioConfigRef.current = config;
            hasShownDisclaimerRef.current = true;

            // Trigger the disclaimer modal
            setCopilotModalAfterDisclaimer(true);
        }

        // Reset the flag when scenario no longer matches
        if (!matchingScenario) {
            hasShownDisclaimerRef.current = false;
            pendingScenarioConfigRef.current = null;
        }
    }, [
        appState,
        copilotActivatedDisclaimer,
        dispatch,
        matchingScenario,
        setCopilotBarProps,
        setCopilotModalAfterDisclaimer,
    ]);

    // Explicitly close the bar when scenario no longer matches
    useEffect(() => {
        if (!matchingScenario && setCopilotBarProps) {
            prevStateRef.current = INITIAL_COPILOT_BAR_STATE;
            setCopilotBarProps(INITIAL_COPILOT_BAR_STATE);
        }
    }, [matchingScenario, setCopilotBarProps]);

    // Update CopilotContext when state changes (for reactive updates after bar is open)
    useEffect(() => {
        if (!setCopilotBarProps) return;

        const newProps = { ...copilotBarState };

        // Only update if values actually changed (excluding function comparisons in chatSetup.cb)
        const { chatSetup: prevChatSetup, ...prevComparable } = prevStateRef.current;
        const { chatSetup: newChatSetup, ...newComparable } = newProps;

        // Compare chatSetup without cb (callbacks)
        const { cb: prevCb, ...prevChatSetupComparable } = prevChatSetup || {};
        const { cb: newCb, ...newChatSetupComparable } = newChatSetup || {};

        const chatSetupChanged = !isEqual(prevChatSetupComparable, newChatSetupComparable);
        const propsChanged = !isEqual(prevComparable, newComparable);

        if (chatSetupChanged || propsChanged) {
            prevStateRef.current = newProps;
            setCopilotBarProps(newProps);
        }
    }, [copilotBarState, setCopilotBarProps]);

    // Manage body class for CopilotBar state
    useEffect(() => {
        if (document.body) {
            const { classList } = document.body;

            const shouldHaveOnClass = isOpen;
            const shouldHaveLoadingClass =
                isOpen && loadingState === COPILOT_BAR_LOADING_STATES.LOADING;
            const shouldHaveMinimizedClass = isOpen && isMinimized;

            classList.toggle(COPILOT_BAR_ON_CLASS, shouldHaveOnClass);
            classList.toggle(COPILOT_BAR_LOADING_CLASS, shouldHaveLoadingClass);
            classList.toggle(COPILOT_BAR_MINIMIZED_CLASS, shouldHaveMinimizedClass);
        }

        return () => {
            document.body?.classList.remove(
                COPILOT_BAR_ON_CLASS,
                COPILOT_BAR_LOADING_CLASS,
                COPILOT_BAR_MINIMIZED_CLASS,
            );
        };
    }, [isMinimized, isOpen, loadingState]);

    // Hide/show userflow widget when CopilotBar opens/closes
    useEffect(() => {
        if (isOpen) {
            userflowHideAndShow('hide');
        } else {
            userflowHideAndShow('show');
        }
    }, [isOpen]);

    // Call disclaimer onClose when bar closes
    useEffect(() => {
        if (!isOpen) {
            copilotDisclaimerOnClose();
        }
    }, [copilotDisclaimerOnClose, isOpen]);

    // ========================================
    // RENDER
    // ========================================
    return (
        <>
            <CopilotBarComponent className="ssm-copilot-bar" />
            {renderCopilotDisclaimer}
        </>
    );
};

export default memo(CopilotBar);
